import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useSession } from "./useSession";

type Settlement = { joinedOrganizationId: string | null };

/**
 * Auto-accepte une invitation pending matchant l'email de l'user
 * fraîchement connecté. Monté par RequireAuth ET par RequireOrganization
 * (donc tourne dès qu'une session existe : magic link callback OU login
 * standard OU reload de page avec session déjà établie).
 *
 * Modélisé en `useQuery` plutôt qu'en `useEffect` pour deux raisons :
 *  1. `isSettling` est observable par les guards — RequireOrganization doit
 *     attendre le verdict avant de rediriger vers /onboarding, sinon un
 *     invité légitime atterrit sur « Donnez un nom à votre espace ».
 *  2. La déduplication par queryKey (l'id utilisateur) remplace le ref
 *     guard : deux composants peuvent monter le hook, les RPC ne partent
 *     qu'une fois.
 *
 * Les effets de bord (toast + refetch du membership) vivent dans la queryFn
 * exprès : c'est le seul endroit qui s'exécute exactement une fois par
 * session, quel que soit le nombre d'observers.
 *
 * Idempotent et silencieux quand il n'y a rien à faire. Si l'utilisateur a
 * plusieurs invitations pending, seule la plus récente est traitée (cf. RPC
 * SQL `find_invitation_for_current_user` qui ORDER BY created_at DESC LIMIT 1).
 */
export function useAcceptPendingInvitation() {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const token = session?.access_token ?? null;

  const query = useQuery({
    // Clé sur l'id utilisateur et NON sur le jeton : une clé de requête se
    // retrouve dans tous les outils d'observabilité (devtools React Query,
    // traceur `?debug=perf`), et un rapport exporté a réellement exposé un
    // bearer token complet, email compris. L'id utilisateur suffit à la
    // déduplication, et il est stable au renouvellement du jeton — ce qui
    // évite en prime une RPC redondante à chaque rafraîchissement de session.
    queryKey: ["invitation-settlement", session?.user?.id ?? null],
    enabled: Boolean(session?.user?.id && token),
    // Une seule tentative par session : ni retry, ni refetch au remount.
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async (): Promise<Settlement> => {
      const { data: rows, error: findErr } = await supabase
        .schema("app")
        .rpc("find_invitation_for_current_user");
      if (findErr) {
        console.warn("[invitation] find failed", findErr);
        return { joinedOrganizationId: null };
      }
      const invitation = Array.isArray(rows) ? rows[0] : rows;
      if (!invitation) return { joinedOrganizationId: null };

      const { data: orgId, error: acceptErr } = await supabase
        .schema("app")
        .rpc("accept_invitation", { p_invitation_id: invitation.id });
      if (acceptErr) {
        console.warn("[invitation] accept failed", acceptErr);
        toast.error("Invitation non acceptée", {
          description: acceptErr.message,
        });
        return { joinedOrganizationId: null };
      }

      // refetch (et non invalidate) : on attend que le membership soit
      // réellement en cache avant de rendre la main, sinon le guard voit
      // encore `null` et redirige vers l'onboarding.
      //
      // cancelQueries d'abord : si le 1er fetch du membership est encore en
      // vol (il a démarré avant l'acceptation, donc il ramènera `null`),
      // refetchQueries se contenterait de s'y raccrocher et on afficherait
      // l'onboarding à un invité déjà membre.
      await queryClient.cancelQueries({ queryKey: ["my-membership"] });
      await queryClient.refetchQueries({ queryKey: ["my-membership"] });
      await queryClient.invalidateQueries({ queryKey: ["team"] });

      toast.success(
        invitation.organization_name
          ? `Bienvenue dans ${invitation.organization_name}`
          : "Invitation acceptée",
      );

      return { joinedOrganizationId: (orgId as string | null) ?? null };
    },
  });

  return {
    /** true tant qu'on ne sait pas si l'user rejoint une org via invitation. */
    isSettling: query.isPending && query.fetchStatus !== "idle",
    joinedOrganizationId: query.data?.joinedOrganizationId ?? null,
  };
}
